
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Share, X } from "lucide-react";
import { toast } from "sonner";
import type { JogoGerado } from "@/types/softwareTurbinado";

type JogosGeradosModalProps = {
  isOpen: boolean;
  onClose: () => void;
  jogos: JogoGerado | null;
};

const JogosGeradosModal = ({ isOpen, onClose, jogos }: JogosGeradosModalProps) => {
  const handleDownload = () => {
    if (!jogos) return;

    const content = `
JOGOS GERADOS - SOFTWARE TURBINADO
Data: ${new Date(jogos.created_at).toLocaleDateString('pt-BR')}
🎯 JOGOS PARA APOSTAR POR 5 DIAS 🎯

MILHAR (12 jogos):
${jogos.milhar.join(', ')}

CENTENAS (12 jogos):
${jogos.centenas.join(', ')}

DEZENAS (7 jogos):
${jogos.dezenas.join(', ')}

TERNO DEZENAS (7 jogos):
${jogos.terno_dezenas.join(', ')}

DUQUES DEZENAS (7 jogos):
${jogos.duques_dezenas.join(', ')}

DUPLAS GRUPO (7 jogos):
${jogos.duplas_grupo.join(', ')}

TERNOS GRUPO (7 jogos):
${jogos.ternos_grupo.join(', ')}

🔥 Gerado pelo Software Turbinado - Palpite Guru
💡 Aposte com responsabilidade por 5 dias consecutivos
    `;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jogos_turbinado_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Jogos baixados com sucesso!");
  };

  const handleShare = async () => {
    if (!jogos) return;

    const text = `🎯 JOGOS SOFTWARE TURBINADO - APOSTE POR 5 DIAS 🎯

MILHAR: ${jogos.milhar.slice(0, 6).join(', ')}...
CENTENAS: ${jogos.centenas.slice(0, 6).join(', ')}...
DEZENAS: ${jogos.dezenas.join(', ')}

🔥 Gerado pelo Software Turbinado - Palpite Guru
💡 Aposte com responsabilidade!`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Jogos Software Turbinado - 5 Dias',
          text
        });
        toast.success("Jogos compartilhados!");
      } catch (error) {
        console.error('Erro ao compartilhar:', error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Jogos copiados para a área de transferência!");
      } catch (error) {
        toast.error("Erro ao copiar jogos");
      }
    }
  };

  if (!jogos) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between text-2xl">
            🎯 JOGOS TURBINADOS - APOSTE POR 5 DIAS
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="text-center bg-gradient-to-r from-purple-500 to-blue-500 text-white p-4 rounded-lg">
            <h3 className="text-xl font-bold">SOFTWARE TURBINADO ATIVADO!</h3>
            <p className="text-sm">Jogos otimizados para apostar por 5 dias consecutivos</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-center">MILHAR (12)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2">
                  {jogos.milhar.map((numero, index) => (
                    <div key={index} className="bg-gradient-to-r from-purple-500 to-blue-500 text-white p-2 rounded text-center">
                      <span className="prediction-number text-lg font-bold">{numero}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-center">CENTENAS (12)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2">
                  {jogos.centenas.map((numero, index) => (
                    <div key={index} className="bg-gradient-to-r from-green-500 to-teal-500 text-white p-2 rounded text-center">
                      <span className="prediction-number text-lg font-bold">{numero}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-center">DEZENAS (7)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {jogos.dezenas.map((numero, index) => (
                    <div key={index} className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-2 rounded text-center">
                      <span className="prediction-number text-lg font-bold">{numero}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-center">TERNO DEZENAS (7)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {jogos.terno_dezenas.map((numero, index) => (
                    <div key={index} className="bg-gradient-to-r from-pink-500 to-purple-500 text-white p-2 rounded text-center">
                      <span className="prediction-number text-sm font-bold">{numero}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-center">DUQUES DEZENAS (7)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {jogos.duques_dezenas.map((numero, index) => (
                    <div key={index} className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white p-2 rounded text-center">
                      <span className="prediction-number text-sm font-bold">{numero}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-center">DUPLAS GRUPO (7)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {jogos.duplas_grupo.map((numero, index) => (
                    <div key={index} className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-2 rounded text-center">
                      <span className="prediction-number text-sm font-bold">{numero}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="md:col-span-2 lg:col-span-3">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-center">TERNOS GRUPO (7)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {jogos.ternos_grupo.map((numero, index) => (
                    <div key={index} className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white p-2 rounded text-center">
                      <span className="prediction-number text-sm font-bold">{numero}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h4 className="font-bold text-yellow-800 mb-2">💡 Como usar os jogos turbinados:</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Aposte nos jogos gerados por 5 dias consecutivos</li>
              <li>• Use as combinações de acordo com sua estratégia de apostas</li>
              <li>• Os números foram otimizados baseados nos últimos 10 resultados</li>
              <li>• Aposte com responsabilidade e dentro de suas possibilidades</li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Button onClick={handleDownload} className="btn-gradient flex-1">
            <Download className="h-4 w-4 mr-2" />
            Baixar Jogos (PDF)
          </Button>
          <Button onClick={handleShare} variant="outline" className="flex-1">
            <Share className="h-4 w-4 mr-2" />
            Compartilhar (Imagem)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JogosGeradosModal;
